// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Alert, Anchor, Button, Card, Group, Loader, Stack, Text, Title } from '@mantine/core';
import type { DocumentReference, MedicationRequest, Patient } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile, useResource } from '@medplum/react';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

function getBinaryDownloadUrl(url: string): string {
  const path = url.split(/[?#]/, 1)[0].replace(/\/+$/, '');
  const segments = path.split('/').filter(Boolean);
  const index = segments.findIndex((segment) => segment.toLowerCase() === 'binary');
  return index >= 0 && segments[index + 1] ? `Binary/${decodeURIComponent(segments[index + 1])}` : url;
}

export function PrescriptionDocumentPage(): JSX.Element {
  const { prescriptionId } = useParams();
  const navigate = useNavigate();
  const medplum = useMedplum();
  const patient = useMedplumProfile() as Patient | undefined;
  const prescription = useResource<MedicationRequest>(
    prescriptionId ? { reference: `MedicationRequest/${prescriptionId}` } : undefined
  );
  const [document, setDocument] = useState<DocumentReference>();
  const [pdfUrl, setPdfUrl] = useState<string>();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let disposed = false;
    const load = async (): Promise<void> => {
      if (!patient?.id || !prescriptionId) {
        setLoading(false);
        return;
      }
      const documents = (await medplum.searchResources('DocumentReference', {
        subject: `Patient/${patient.id}`,
        _count: '100',
      })) as DocumentReference[];
      const found = documents.find((candidate) =>
        candidate.context?.related?.some((related) => related.reference === `MedicationRequest/${prescriptionId}`)
      );
      if (!found || disposed) {
        setLoading(false);
        return;
      }
      setDocument(found);
      const attachment = found.content?.[0]?.attachment;
      const binaryUrl = attachment?.url;
      let blob: Blob | undefined;
      if (attachment?.data) {
        blob = new Blob([Uint8Array.from(atob(attachment.data), (character) => character.charCodeAt(0))], {
          type: attachment.contentType ?? 'application/pdf',
        });
      } else if (binaryUrl) {
        blob = await medplum.download(getBinaryDownloadUrl(binaryUrl));
      }
      if (blob) {
        if (!disposed) {
          setPdfUrl(URL.createObjectURL(blob));
        }
      }
      if (!disposed) {
        setLoading(false);
      }
    };
    load().catch(() => setLoading(false));
    return () => {
      disposed = true;
      setPdfUrl((current) => {
        if (current) {
          URL.revokeObjectURL(current);
        }
        return undefined;
      });
    };
  }, [medplum, patient?.id, prescriptionId]);

  if (!patient || !prescription) {
    return (
      <Stack align="center" p="xl">
        <Loader />
      </Stack>
    );
  }
  return (
    <Stack maw={1100} mx="auto" p="md">
      <Group justify="space-between">
        <div>
          <Title order={2}>Prescription document</Title>
          <Text c="dimmed">{prescription.medicationCodeableConcept?.text ?? 'Signed prescription'}</Text>
        </div>
        <Button variant="default" onClick={() => navigate('/my-chart')}>
          Back to My Chart
        </Button>
      </Group>
      <Card withBorder>
        {loading && (
          <Group justify="center" p="xl">
            <Loader />
          </Group>
        )}
        {!loading && !document && (
          <Alert color="yellow" title="Document unavailable">
            This prescription has not produced a signed document yet.
          </Alert>
        )}
        {pdfUrl && (
          <iframe title="Signed prescription PDF" src={pdfUrl} style={{ width: '100%', height: '75vh', border: 0 }} />
        )}
        {document && pdfUrl && (
          <Group justify="flex-end" mt="sm">
            <Anchor href={pdfUrl} target="_blank" rel="noreferrer">
              Open signed PDF
            </Anchor>
            <Anchor href={pdfUrl} download="signed-prescription.pdf">
              Download PDF
            </Anchor>
          </Group>
        )}
      </Card>
    </Stack>
  );
}
